// AdminCohortChallenges — admin-only CRUD panel for cohort challenges.
// Mounted inside /admin/analytics. Lets the platform owner create themed
// challenges (e.g., "March SuPER Sprint") with date windows + prize text.
import React, { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  bg: "#060606", card: "#0d1218", border: "#1C1C1C",
  gold: "#C9A227", text: "#EDE8DC", mute: "#AAAAAA",
  green: "#22C55E", red: "#FF4444",
};

const empty = () => ({
  title: "",
  description: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10),
  prize_text: "",
  is_active: true,
});

export default function AdminCohortChallenges() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | {id?, ...form}

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem("flexx_access_token");
      const res = await axios.get(`${API}/admin/cohort-challenges`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setList(res.data.challenges || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    setError(null);
    try {
      const token = localStorage.getItem("flexx_access_token");
      const headers = { Authorization: `Bearer ${token}` };
      const body = { ...editing };
      delete body.id;
      // ISO 8601: pad date to start/end of day for clean window math
      if (body.start_date && body.start_date.length === 10) body.start_date = `${body.start_date}T00:00:00Z`;
      if (body.end_date && body.end_date.length === 10) body.end_date = `${body.end_date}T23:59:59Z`;
      if (editing.id) {
        await axios.patch(`${API}/admin/cohort-challenges/${editing.id}`, body, { headers });
      } else {
        await axios.post(`${API}/admin/cohort-challenges`, body, { headers });
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this challenge? This cannot be undone.")) return;
    try {
      const token = localStorage.getItem("flexx_access_token");
      await axios.delete(`${API}/admin/cohort-challenges/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div data-testid="admin-cohort-challenges" style={{
      background: T.card, border: `1px solid ${T.border}`,
      borderLeft: `3px solid ${T.gold}`, borderRadius: 12, padding: 18,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: "monospace", color: T.gold, fontSize: 10, letterSpacing: 2 }}>🎯 ADMIN · COHORT CHALLENGES</div>
          <div style={{ color: T.text, fontSize: 18, fontWeight: 800, marginTop: 4 }}>Themed XP Sprints</div>
        </div>
        <button data-testid="cohort-challenge-new-btn" onClick={() => setEditing(empty())}
          style={btnPrimary}>+ New Challenge</button>
      </div>

      {error && (
        <div data-testid="cohort-challenge-error" style={{
          padding: 10, borderRadius: 8, background: `${T.red}15`,
          border: `1px solid ${T.red}55`, color: T.red, fontSize: 13, marginBottom: 12,
        }}>{error}</div>
      )}

      {loading && <div style={{ color: T.mute, fontSize: 12 }}>Loading…</div>}

      {!loading && list.length === 0 && !editing && (
        <div data-testid="cohort-challenge-empty" style={{
          padding: 14, borderRadius: 8, background: "#0a0e13",
          border: `1px dashed ${T.border}`, color: T.mute, fontSize: 13, textAlign: "center",
        }}>
          No challenges yet. Click <b style={{ color: T.gold }}>+ New Challenge</b> to create your first cohort sprint.
        </div>
      )}

      <div style={{ display: "grid", gap: 8 }}>
        {list.map((c) => (
          <ChallengeRow key={c.id} c={c} onEdit={() => setEditing({ ...c })} onDelete={() => del(c.id)} />
        ))}
      </div>

      {editing && (
        <ChallengeForm form={editing} setForm={setEditing} onSave={save} onCancel={() => setEditing(null)} />
      )}
    </div>
  );
}

function ChallengeRow({ c, onEdit, onDelete }) {
  const now = Date.now();
  const start = new Date(c.start_date).getTime();
  const end = new Date(c.end_date).getTime();
  const status =
    !c.is_active ? { label: "PAUSED", color: T.mute } :
    now < start ? { label: "UPCOMING", color: "#FFB347" } :
    now > end ? { label: "ENDED", color: T.mute } :
    { label: "LIVE", color: T.green };
  return (
    <div data-testid={`cohort-challenge-row-${c.id}`} style={{
      display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 10, alignItems: "center",
      padding: 12, borderRadius: 8, background: "#0a0e13", border: `1px solid ${T.border}`,
    }}>
      <div>
        <div style={{ color: T.text, fontWeight: 800, fontSize: 14 }}>{c.title}</div>
        <div style={{ color: T.mute, fontSize: 11, fontFamily: "monospace", marginTop: 4 }}>
          {(c.start_date || "").slice(0, 10)} → {(c.end_date || "").slice(0, 10)}
        </div>
      </div>
      <div data-testid={`cohort-challenge-status-${c.id}`} style={{
        padding: "3px 10px", borderRadius: 999,
        background: `${status.color}15`, border: `1px solid ${status.color}`,
        color: status.color, fontFamily: "monospace", fontSize: 9, letterSpacing: 1.5, fontWeight: 800,
      }}>{status.label}</div>
      <button data-testid={`cohort-challenge-edit-${c.id}`} onClick={onEdit} style={btnGhost}>Edit</button>
      <button data-testid={`cohort-challenge-delete-${c.id}`} onClick={onDelete}
        style={{ ...btnGhost, color: T.red, borderColor: `${T.red}55` }}>Delete</button>
    </div>
  );
}

function ChallengeForm({ form, setForm, onSave, onCancel }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div data-testid="cohort-challenge-form" style={{
      marginTop: 14, padding: 14, borderRadius: 10,
      background: "#0a0e13", border: `1px solid ${T.gold}55`,
    }}>
      <div style={{ fontFamily: "monospace", color: T.gold, fontSize: 10, letterSpacing: 2, marginBottom: 10 }}>
        {form.id ? "EDIT CHALLENGE" : "NEW CHALLENGE"}
      </div>
      <Field label="Title">
        <input data-testid="cohort-form-title" value={form.title} onChange={set("title")} style={input} placeholder="March SuPER Sprint" />
      </Field>
      <Field label="Description">
        <textarea data-testid="cohort-form-description" rows={2} value={form.description} onChange={set("description")}
          style={{ ...input, fontFamily: "inherit", resize: "vertical" }}
          placeholder="Earn the most XP this month. Top 3 win a 1:1 review with AXEL." />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Start Date">
          <input data-testid="cohort-form-start" type="date" value={(form.start_date || "").slice(0, 10)} onChange={set("start_date")} style={input} />
        </Field>
        <Field label="End Date">
          <input data-testid="cohort-form-end" type="date" value={(form.end_date || "").slice(0, 10)} onChange={set("end_date")} style={input} />
        </Field>
      </div>
      <Field label="Prize">
        <input data-testid="cohort-form-prize" value={form.prize_text} onChange={set("prize_text")} style={input}
          placeholder="🏆 1:1 AXEL review + FLEXX hoodie" />
      </Field>
      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, color: T.text, fontSize: 13 }}>
        <input data-testid="cohort-form-active" type="checkbox" checked={!!form.is_active} onChange={set("is_active")} />
        Active (visible to traders)
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
        <button data-testid="cohort-form-cancel" onClick={onCancel} style={btnGhost}>Cancel</button>
        <button data-testid="cohort-form-save" onClick={onSave} style={btnPrimary}>
          {form.id ? "Save Changes" : "Create Challenge"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 10 }}>
      <div style={{ color: T.mute, fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontFamily: "monospace", marginBottom: 6 }}>{label}</div>
      {children}
    </label>
  );
}

const input = {
  width: "100%", padding: "10px 12px", borderRadius: 8,
  background: "#13161c", border: `1px solid ${T.border}`,
  color: T.text, fontSize: 13, outline: "none", boxSizing: "border-box",
  fontFamily: "monospace",
};
const btnPrimary = {
  all: "unset", cursor: "pointer",
  padding: "8px 16px", borderRadius: 6,
  background: `linear-gradient(135deg, ${T.gold}, #E8B94F)`,
  color: "#0a0a0a", fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5, fontWeight: 800,
};
const btnGhost = {
  all: "unset", cursor: "pointer",
  padding: "8px 14px", borderRadius: 6,
  background: "transparent", border: `1px solid ${T.border}`,
  color: T.mute, fontFamily: "monospace", fontSize: 11, letterSpacing: 1,
};
